export interface BundleVersion {
  major: number;
  minor: number;
  patch: number;
  build: number;
  timestamp: number;
}

/**
 * Le versioni OTA includono sempre semver, build e timestamp; l'APK di base
 * può avere soltanto il semver. Il canale beta non determina l'ordinamento:
 * il bundle promosso allo stable conserva infatti lo stesso identificativo.
 */
export function parseBundleVersion(value: string): BundleVersion | null {
  const match = /^(\d+)\.(\d+)\.(\d+)(?:\.(\d+))?(?:-beta)?(?:-(\d{8})-(\d{6}))?$/.exec(value);
  if (!match) return null;

  const [, major, minor, patch, build = '0', date = '0', time = '0'] = match;
  const numeric = [major, minor, patch, build, date, time].map(Number);
  if (!numeric.every(Number.isSafeInteger)) return null;

  return {
    major: numeric[0],
    minor: numeric[1],
    patch: numeric[2],
    build: numeric[3],
    timestamp: numeric[4] * 1_000_000 + numeric[5],
  };
}

/** Returns a positive value only when `candidate` is newer than `current`. */
export function compareBundleVersions(current: string, candidate: string): number | null {
  const parsedCurrent = parseBundleVersion(current);
  const parsedCandidate = parseBundleVersion(candidate);
  if (!parsedCurrent || !parsedCandidate) return null;

  for (const key of ['major', 'minor', 'patch', 'build', 'timestamp'] as const) {
    if (parsedCandidate[key] !== parsedCurrent[key]) {
      return parsedCandidate[key] > parsedCurrent[key] ? 1 : -1;
    }
  }
  return 0;
}

export function isUpdateAllowed(
  current: string,
  candidate: string,
  allowDowngrade = false,
): boolean {
  const comparison = compareBundleVersions(current, candidate);
  return comparison === 1 || (comparison === -1 && allowDowngrade);
}
